import { AiOutlined, DownloadUniversal } from '@duxton-web/icons';
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Flex,
  Modal,
  Row,
  Spin,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from '@duxton-web/ui';
import React, { useEffect, useMemo, useState } from 'react';

import type { GenerationContext } from './ConfigContentGeneration.helpers';
import {
  buildContentConfigGenerationCsv,
  contentGenerationFilename,
  downloadContentConfigGenerationCsv,
  parseContentConfigGenerationResults,
} from './ConfigContentGeneration.helpers';
import { contentConfigModalStyle } from './theme';

interface Props {
  attributes: string[];
  attributeDefinitions: Record<string, string>;
  context: GenerationContext;
  isFailure: boolean;
  isLoading: boolean;
  onClose: () => void;
  onRegenerate: (attributes?: string[]) => void;
  onRegenerateAttribute: (attribute: string) => void;
  onRetry: () => void;
  open: boolean;
  readyAttributes: string[];
  responsesByAttribute: Record<string, any>;
  usesActiveConfig: boolean;
}

const ContentGenerationModal = ({
  attributes,
  attributeDefinitions,
  context,
  isFailure,
  isLoading,
  onClose,
  onRegenerate,
  onRegenerateAttribute,
  onRetry,
  open,
  readyAttributes,
  responsesByAttribute,
  usesActiveConfig,
}: Props) => {
  const [activeAttribute, setActiveAttribute] = useState('');
  const readyAttributeSet = useMemo(() => {
    const displayedAttributes = new Set(attributes);
    return new Set(readyAttributes.filter((attribute) => displayedAttributes.has(attribute)));
  }, [attributes, readyAttributes]);
  const itemsByAttribute = useMemo(
    () => parseContentConfigGenerationResults(attributes, responsesByAttribute),
    [attributes, responsesByAttribute],
  );
  const totalItems = attributes.reduce(
    (total, attribute) => total + (
      readyAttributeSet.has(attribute) ? (itemsByAttribute[attribute] || []).length : 0
    ),
    0,
  );

  useEffect(() => {
    if (!open) return;
    if (!attributes.includes(activeAttribute)) {
      setActiveAttribute(attributes[0] || '');
    }
  }, [activeAttribute, attributes, open]);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development' || !open) return undefined;
    const ignoreResizeObserverDeliveryWarning = (event: ErrorEvent) => {
      if (event.message === 'ResizeObserver loop completed with undelivered notifications.') {
        event.stopImmediatePropagation();
      }
    };
    window.addEventListener('error', ignoreResizeObserverDeliveryWarning, true);
    return () => window.removeEventListener('error', ignoreResizeObserverDeliveryWarning, true);
  }, [open]);

  const download = (downloadAttributes: string[], attribute?: string) => {
    const csv = buildContentConfigGenerationCsv(
      context,
      downloadAttributes,
      itemsByAttribute,
    );
    downloadContentConfigGenerationCsv(
      contentGenerationFilename(context.product, attribute),
      csv,
    );
  };

  const tabs = attributes.map((attribute) => {
    const items = itemsByAttribute[attribute] || [];
    const definition = attributeDefinitions[attribute];
    const isReady = readyAttributeSet.has(attribute);
    let attributeResult;
    if (!isReady) {
      attributeResult = (
        <Flex vertical align="center" justify="center" gap={8} className="cc-generation-tab-waiting">
          {isLoading && <Spin />}
          <Typography.Body level={2} type="secondary">
            {isFailure ? 'No result was returned for this attribute.' : 'Waiting for this attribute…'}
          </Typography.Body>
        </Flex>
      );
    } else if (items.length === 0) {
      attributeResult = <Empty description="No content was returned for this attribute." />;
    } else {
      attributeResult = (
        <Row gutter={[16, 16]}>
          {items.map((item, index) => {
            const failedReview = item.reviewResult === 'fail';
            return (
              <Col key={`${attribute}-${index}`} xs={24} md={12}>
                <Card bordered={false} className="cc-generation-card">
                  <Flex vertical gap="middle" className="cc-generation-card-content">
                    <div className="cc-generation-copy">
                      <Typography.Body level={2} strong>{item.title}</Typography.Body>
                      <Typography.Body level={2} type="secondary" className="cc-generation-message">
                        {item.message}
                      </Typography.Body>
                    </div>
                    <Flex justify="space-between" align="center" gap={8} wrap="wrap">
                      {item.cvp ? <Tag>CVP: {item.cvp}</Tag> : <span />}
                      <Tooltip title={item.reviewReason}>
                        <Tag color={failedReview ? 'error' : 'success'}>Score: {item.score}</Tag>
                      </Tooltip>
                    </Flex>
                  </Flex>
                </Card>
              </Col>
            );
          })}
        </Row>
      );
    }
    return {
      key: attribute,
      label: (
        <span
          className={isReady
            ? 'cc-generation-tab-label cc-generation-tab-label-ready'
            : 'cc-generation-tab-label cc-generation-tab-label-waiting'}
        >
          <span>{attribute}</span>
          {isReady && (
            <span className="cc-generation-tab-count">· {items.length}</span>
          )}
        </span>
      ),
      children: (
        <Flex vertical gap="middle" className="cc-generation-panel">
          <Flex vertical gap={12}>
            <div>
              <Typography.Body level={2} strong>{attribute}</Typography.Body>
              {definition && (
                <Typography.Metadata type="secondary" className="cc-generation-definition">
                  {definition}
                </Typography.Metadata>
              )}
            </div>
            <Flex gap={8} wrap="wrap">
              <Button
                size="small"
                icon={<AiOutlined />}
                disabled={isLoading}
                onClick={() => onRegenerateAttribute(attribute)}
              >
                Regenerate attribute
              </Button>
              <Button
                size="small"
                icon={<DownloadUniversal />}
                disabled={!isReady || items.length === 0}
                onClick={() => download([attribute], attribute)}
              >
                Download attribute CSV
              </Button>
            </Flex>
          </Flex>

          {attributeResult}
        </Flex>
      ),
    };
  });

  let generationStatus = null;
  if (isLoading) {
    generationStatus = (
      <Flex align="center" gap={8}>
        <Spin size="small" />
        <Typography.Body level={2} type="secondary">
          {readyAttributeSet.size} of {attributes.length} ready
        </Typography.Body>
      </Flex>
    );
  } else if (isFailure) {
    generationStatus = (
      <Flex vertical gap={8}>
        <Alert
          type="error"
          showIcon
          message="Some content could not be generated. Ready attributes are still available below."
        />
        <Flex>
          <Button type="primary" icon={<AiOutlined />} onClick={onRetry}>Try again</Button>
        </Flex>
      </Flex>
    );
  }

  return (
    <Modal
      title={(
        <Flex align="center" gap={8}>
          <AiOutlined />
          <span>Generated Content</span>
          {!isLoading && !isFailure && <Tag>{totalItems}</Tag>}
        </Flex>
      )}
      open={open}
      onCancel={onClose}
      maskClosable={false}
      keyboard={false}
      width={1040}
      style={contentConfigModalStyle}
      className="cc-modal cc-generation-modal"
      footer={(
        <Flex justify="space-between" align="center" gap={12} wrap="wrap">
          <Typography.Metadata type="secondary">
            {attributes.length} {attributes.length === 1 ? 'attribute' : 'attributes'} · {totalItems} results
          </Typography.Metadata>
          <Flex gap={8}>
            <Button onClick={onClose}>Close</Button>
            <Button
              icon={<AiOutlined />}
              disabled={isLoading}
              onClick={() => onRegenerate(attributes)}
            >
              Regenerate content
            </Button>
            <Button
              type="primary"
              icon={<DownloadUniversal />}
              disabled={isLoading
                || isFailure
                || readyAttributeSet.size !== attributes.length
                || totalItems === 0}
              onClick={() => download(attributes)}
            >
              Download all CSV
            </Button>
          </Flex>
        </Flex>
      )}
    >
      <Flex vertical gap="middle">
        <Typography.Metadata type="secondary">
          {context.product} · {context.country} · {context.language.toUpperCase()}
        </Typography.Metadata>
        {usesActiveConfig && (
          <Alert
            type="info"
            showIcon
            message="This product is PENDING APPROVAL. Content is generated using the ACTIVE configuration."
          />
        )}
        {generationStatus}
        <Tabs
          type="pill"
          activeKey={activeAttribute}
          onChange={setActiveAttribute}
          items={tabs}
        />
      </Flex>
    </Modal>
  );
};

export default ContentGenerationModal;
